import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';

import { API_BASE as BASE } from '../lib/apiBase';
import { EN_GENERATED } from '../i18n/enGenerated';
import { ES_GENERATED } from '../i18n/esGenerated';
import { EN_PUBLIC_EXTRA, ES_PUBLIC_EXTRA } from '../i18n/publicExtra';
const STORAGE_LANG = 'monteurcentraal_lang';
const STORAGE_CACHE = 'monteurcentraal_i18n_cache_v2';

// Dot-notatie identifiers (bv. "nav.diagnose", "page.dashboard.title") zijn i18n-keys,
// GEEN vertaalbare Nederlandse tekst. Die mogen nooit naar Gemini-autofill (anders echo't
// het model de key terug en vervuilt de cache). Ze vallen terug op FALLBACK of de inline fallback.
const isI18nKey = (k) => /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)+$/.test(k);

// Bundled minimal fallback voor NL en EN zodat de app werkt zonder backend roundtrip.
// Vertalingen die hier ontbreken vallen terug op de string-key zelf.
const FALLBACK = {
  nl: {
    'nav.dashboard': 'Dashboard', 'nav.search': 'Zoeken', 'nav.workcard': 'Werkkaart',
    'nav.customers': 'Klanten', 'nav.receipts': 'Bonnen', 'nav.payments': 'Betalingen',
    'nav.cartrade': 'Auto handel', 'nav.planning': 'Planning', 'nav.alerts': 'Meldingen',
    'nav.parts': 'Onderdelen', 'nav.reporting': 'Rapportage', 'nav.settle': 'Verrekenen',
    'nav.documents': 'Documenten', 'nav.layout': 'Bon layout', 'nav.garage': 'Gegevens',
    'nav.settings': 'Instellingen',
    'nav.diagnose': 'AI Diagnose', 'nav.accounting': 'Boekhouden', 'nav.debtors': 'Debiteurenbeheer',
    'nav.expenses': 'Kosten / Inkoopfacturen', 'nav.reminders': 'Automatische herinneringen',
    'nav.vatprofit': 'BTW & Winst', 'nav.ledger': 'Grootboek', 'nav.balance': 'Balans',
    'nav.bankimport': 'Bankimport', 'nav.onlinebooking': 'Online afspraken', 'nav.admaker': 'Advertentie maken',
    'nav.partstrade': 'Onderdelenhandel', 'nav.oh_stock': 'Voorraad', 'nav.oh_used': 'Gebruikte onderdelen',
    'nav.oh_new': 'Nieuwe onderdelen', 'nav.oh_scrap': "Sloopauto's", 'nav.oh_ads': 'Advertenties',
    'nav.oh_value': 'Waardebepaling', 'nav.oh_history': 'Verkoophistorie',
    'page.dashboard.title': 'Dashboard', 'page.dashboard.sub': 'Overzicht van uw garage',
    'page.customers.title': 'Klanten', 'page.customers.sub': 'Klantenbeheer met voertuigen en historie',
    'page.receipts.title': 'Bonnen', 'page.receipts.sub': 'Offertes, werkbonnen en facturen',
    'page.payments.title': 'Betalingen', 'page.payments.sub': 'Geregistreerde betalingen en openstaande facturen',
    'page.cartrade.title': 'Auto handel', 'page.cartrade.sub': 'Handelsvoorraad en verkochte voertuigen',
    'page.planning.title': 'Planning', 'page.planning.sub': 'Afspraken en werkagenda',
    'page.alerts.title': 'Meldingen', 'page.alerts.sub': 'APK, openstaande betalingen en slapende klanten',
    'page.parts.title': 'Onderdelen', 'page.parts.sub': 'Voorraad, inkoop- en verkoopprijzen',
    'page.reporting.title': 'Rapportage', 'page.reporting.sub': 'Financiële en operationele rapporten',
    'page.settle.title': 'Verrekenen', 'page.settle.sub': 'Winstaandeel per teamlid + voorgeschoten kosten',
    'page.documents.title': 'Documenten', 'page.documents.sub': 'PDF beheer en cloud-synchronisatie',
    'page.layout.title': 'Bon layout', 'page.layout.sub': 'Pas de opmaak van offertes en facturen aan',
    'page.search.title': 'Zoeken', 'page.search.sub': 'Zoek op kenteken, klantnaam, bonnummer of plaats',
    'page.workcard.title': 'Werkkaart', 'page.workcard.sub': 'Open werk en lopende reparaties',
    'page.garage.title': 'Garagegegevens', 'page.garage.sub': 'Wordt gebruikt op alle bonnen, facturen en offertes',
    'page.settings.title': 'Instellingen', 'page.settings.sub': 'Licentie, cloud, team en back-up beheren',
    'tab.general': 'Algemeen', 'tab.plans': 'Plannen', 'tab.languages': 'Talen',
    'tab.license': 'Licentie', 'tab.cloud': 'Cloud sync', 'tab.team': 'Team',
    'tab.smtp': 'SMTP / E-mail', 'tab.backup': 'Back-up', 'tab.notifications': 'Meldingen', 'tab.maillog': 'Mail log',
    // Dashboard keys
    'dash.title': 'Dashboard', 'dash.subtitle': 'Overzicht van uw garage',
    'dash.refresh_alerts': 'Meldingen verversen', 'dash.today': 'Vandaag',
    'dash.this_week': 'Deze week', 'dash.this_month': 'Deze maand', 'dash.this_year': 'Dit jaar',
    'dash.vehicles': 'Voertuigen', 'dash.open_amount': 'Openstaand bedrag',
    'dash.revenue_per_month': 'Omzet per maand (€) - laatste 12 mnd',
    'dash.apk_alerts': 'APK alerts (binnen 60 dagen)',
    'dash.top_customers': 'Top 5 klanten op omzet',
    'dash.top_open': 'Top openstaande facturen',
    'dash.todays_planning': 'Planning vandaag',
    'dash.stock_alerts': 'Voorraad-alerts (onder minimum)',
    'dash.recent_receipts': 'Recente bonnen',
    'dash.quick_actions': 'Snelle acties',
    'dash.audit_trail': 'Audit trail (laatste activiteit)',
    // Table columns
    'col.kenteken': 'Kenteken', 'col.merk_model': 'Merk / Model',
    'col.apk': 'APK', 'col.km': 'KM',
    // Common
    'common.open': 'Open', 'common.type': 'Type',
    // Generic Dutch words used in tables and labels
    'Kenteken': 'Kenteken', 'Merk / Model': 'Merk / Model', 'Bj': 'Bj',
    'Inkoop': 'Inkoop', 'Verkoop': 'Verkoop', 'Marge': 'Marge', 'Status': 'Status',
    'Werkstatus': 'Werkstatus', 'Bonnummer': 'Bonnummer', 'Datum': 'Datum',
    'Klant': 'Klant', 'Klanten': 'Klanten', 'Kentekens': 'Kentekens',
    'Methode': 'Methode', 'Referentie': 'Referentie', 'Bedrag': 'Bedrag',
    'Nummer': 'Nummer', 'Totaal': 'Totaal', 'Betaald': 'Betaald',
    'Openstaand': 'Openstaand', 'Open': 'Open', 'Omschrijving': 'Omschrijving',
    'Aantal': 'Aantal', 'Prijs': 'Prijs', 'Code': 'Code', 'Naam': 'Naam',
    'Voorraad': 'Voorraad', 'Min': 'Min', 'Voorraad / Min': 'Voorraad / Min',
    'Locatie': 'Locatie', 'Gebruiker': 'Gebruiker', 'Actie': 'Actie', 'Detail': 'Detail',
    'Grootte': 'Grootte', 'Plaats': 'Plaats', 'Telefoon': 'Telefoon',
    'Rol': 'Rol', 'Aan': 'Aan', 'Onderwerp': 'Onderwerp',
    'Melding': 'Melding', 'Meldingen': 'Meldingen', 'Prioriteit': 'Prioriteit',
    'Start': 'Start', 'Eind': 'Eind', 'Duur': 'Duur', 'Titel': 'Titel', 'Monteur': 'Monteur',
    'Auto': 'Auto', 'Bon': 'Bon', 'Onderdeel': 'Onderdeel',
    'Persoon': 'Persoon', 'Gekoppelde bon': 'Gekoppelde bon',
    'Winst%': 'Winst%', 'Aandeel omzet': 'Aandeel omzet',
    'Voorgeschoten (open)': 'Voorgeschoten (open)', 'Saldo te ontvangen': 'Saldo te ontvangen',
    'Omzet (lifetime)': 'Omzet (lifetime)', 'Dagen': 'Dagen', 'Bedrag (€)': 'Bedrag (€)',
    'Notitie': 'Notitie', 'Notities': 'Notities',
    'Bouwjaar': 'Bouwjaar', 'Brandstof': 'Brandstof', 'APK vervalt': 'APK vervalt',
    'KM-stand': 'KM-stand', 'Postcode': 'Postcode', 'Huisnr': 'Huisnr',
    'Adres': 'Adres', 'Land': 'Land', 'Iban': 'Iban', 'KvK': 'KvK', 'BTW': 'BTW',
    'Garagenaam': 'Garagenaam', 'Wachtwoord': 'Wachtwoord',
    'Inloggen': 'Inloggen', 'Uitloggen': 'Uitloggen', 'Registreren': 'Registreren',
    'Nieuw': 'Nieuw', 'Reset': 'Reset',
    'Activeren': 'Activeren', 'Deactiveren': 'Deactiveren', 'Koppelen': 'Koppelen',
    'Loskoppelen': 'Loskoppelen', 'Downloaden': 'Downloaden', 'Uploaden': 'Uploaden',
    'Versturen': 'Versturen', 'Verzenden': 'Verzenden', 'Testen': 'Testen',
    'Ja': 'Ja', 'Nee': 'Nee',
    'Vandaag': 'Vandaag', 'Deze week': 'Deze week', 'Deze maand': 'Deze maand', 'Dit jaar': 'Dit jaar',
    'Bonnen verdeling': 'Bonnen verdeling', 'Offertes': 'Offertes', 'Werkbonnen': 'Werkbonnen',
    'Facturen (open)': 'Facturen (open)', 'Facturen': 'Facturen',
    'Geen openstaande facturen.': 'Geen openstaande facturen.',
    'Geen afspraken vandaag.': 'Geen afspraken vandaag.',
    'Plan afspraak': 'Plan afspraak', 'Alle voorraad is op peil.': 'Alle voorraad is op peil.',
    'Nog geen bonnen.': 'Nog geen bonnen.', 'Nog geen klant-omzet geregistreerd.': 'Nog geen klant-omzet geregistreerd.',
    'Afspraak': 'Afspraak', 'Gepland': 'Gepland', 'Bevestigd': 'Bevestigd',
    'In uitvoering': 'In uitvoering', 'Klaar': 'Klaar', 'Geannuleerd': 'Geannuleerd',
    'Concept': 'Concept', 'Bezig...': 'Bezig...', 'Opzoeken': 'Opzoeken',
    'Nieuwe klant': 'Nieuwe klant', 'Nieuwe bon': 'Nieuwe bon', 'Map openen': 'Map openen',
    'geregistreerd': 'geregistreerd', 'klanten geregistreerd': 'klanten geregistreerd',
    'Algemeen': 'Algemeen', 'Plannen': 'Plannen', 'Talen': 'Talen',
    'Licentie': 'Licentie', 'Cloud sync': 'Cloud sync', 'Team': 'Team',
    'Back-up': 'Back-up', 'Mail log': 'Mail log',
    // Common toast / notification strings (NL = identity)
    'Mislukt': 'Mislukt', 'Opslaan mislukt': 'Opslaan mislukt', 'Verwijderen mislukt': 'Verwijderen mislukt',
    'Opslaan': 'Opslaan', 'Verwijderen': 'Verwijderen', 'Annuleren': 'Annuleren', 'Sluiten': 'Sluiten',
    'Toevoegen': 'Toevoegen', 'Bewerken': 'Bewerken', 'Zoeken': 'Zoeken', 'Exporteren': 'Exporteren',
    'Verwijderd': 'Verwijderd', 'Opgeslagen': 'Opgeslagen', 'Toegevoegd': 'Toegevoegd', 'Verrekend': 'Verrekend',
    'Klant verwijderd': 'Klant verwijderd', 'Klant opgeslagen': 'Klant opgeslagen',
    'Voertuig verwijderd': 'Voertuig verwijderd', 'Voertuig opgeslagen': 'Voertuig opgeslagen',
    'Bon aangemaakt': 'Bon aangemaakt', 'Bon opgeslagen': 'Bon opgeslagen', 'Bon verwijderd': 'Bon verwijderd',
    'Onderdeel opgeslagen': 'Onderdeel opgeslagen', 'Onderdeel verwijderd': 'Onderdeel verwijderd',
    'Auto verwijderd': 'Auto verwijderd', 'Auto opgeslagen': 'Auto opgeslagen',
    'Afspraak opgeslagen': 'Afspraak opgeslagen', 'Afspraak verwijderd': 'Afspraak verwijderd',
    'Document verwijderd': 'Document verwijderd', 'Geüpload': 'Geüpload',
    'Betaling toegevoegd': 'Betaling toegevoegd', 'Voorschot toegevoegd': 'Voorschot toegevoegd',
    'Winstaandeel opgeslagen': 'Winstaandeel opgeslagen', 'Medewerker toegevoegd': 'Medewerker toegevoegd',
    'Algemene instellingen opgeslagen': 'Algemene instellingen opgeslagen',
    'Garagegegevens opgeslagen': 'Garagegegevens opgeslagen', 'Adres ingevuld': 'Adres ingevuld',
    'Logo geüpload': 'Logo geüpload', 'Logo verwijderd': 'Logo verwijderd', 'SMTP opgeslagen': 'SMTP opgeslagen',
    'Cloud verbonden!': 'Cloud verbonden!', 'Cloud losgekoppeld': 'Cloud losgekoppeld',
    'Licentie geactiveerd! Cloud functies zijn nu beschikbaar.': 'Licentie geactiveerd! Cloud functies zijn nu beschikbaar.',
    'Licentie gedeactiveerd': 'Licentie gedeactiveerd', 'Back-up gedownload': 'Back-up gedownload',
    'Welkom terug!': 'Welkom terug!', 'Account aangemaakt! Check je e-mail om te activeren.': 'Account aangemaakt! Check je e-mail om te activeren.',
    'Activatiemail verstuurd. Check je inbox.': 'Activatiemail verstuurd. Check je inbox.',
    'Taal verwijderd': 'Taal verwijderd', 'Alle keys zijn al vertaald': 'Alle keys zijn al vertaald',
    'Auto-vertalen mislukt': 'Auto-vertalen mislukt', 'Laden mislukt': 'Laden mislukt',
    'Toevoegen mislukt': 'Toevoegen mislukt', 'Activatie mislukt': 'Activatie mislukt',
    'Back-up mislukt': 'Back-up mislukt', 'Versturen mislukt': 'Versturen mislukt',
    'Verversen mislukt': 'Verversen mislukt', 'OAuth start mislukt': 'OAuth start mislukt',
    'SMTP test mislukt': 'SMTP test mislukt', 'E-mail mislukt': 'E-mail mislukt',
    'Omzetten mislukt': 'Omzetten mislukt', 'Status update mislukt': 'Status update mislukt',
    'Download mislukt': 'Download mislukt', 'Upload mislukt': 'Upload mislukt',
    'RDW lookup mislukt': 'RDW lookup mislukt', 'Adres lookup mislukt': 'Adres lookup mislukt',
    'PDOK lookup mislukt': 'PDOK lookup mislukt', 'Inloggen mislukt': 'Inloggen mislukt',
    'Registratie mislukt': 'Registratie mislukt', 'Opnieuw versturen mislukt': 'Opnieuw versturen mislukt',
    'Adres niet gevonden': 'Adres niet gevonden', 'Kies een klant': 'Kies een klant',
    'Kies een bon': 'Kies een bon', 'Geen klant gekoppeld': 'Geen klant gekoppeld',
    'Kenteken is verplicht': 'Kenteken is verplicht', 'Code is verplicht': 'Code is verplicht',
    'Naam is verplicht': 'Naam is verplicht', 'Vul een titel in': 'Vul een titel in',
    'Vul een bedrag in': 'Vul een bedrag in', 'Vul een omschrijving in': 'Vul een omschrijving in',
    'Ongeldig bedrag': 'Ongeldig bedrag', 'Voer een licentiekey in': 'Voer een licentiekey in',
    'Vul eerst kenteken in': 'Vul eerst kenteken in', 'Vul eerst het kenteken in': 'Vul eerst het kenteken in',
    'Sla eerst de afspraak op': 'Sla eerst de afspraak op',
    'Garagenaam verplicht': 'Garagenaam verplicht', 'Wachtwoord minimaal 6 tekens': 'Wachtwoord minimaal 6 tekens',
    'Vul je e-mail in': 'Vul je e-mail in', 'Licentie vereist': 'Licentie vereist',
    'Popup geblokkeerd - sta popups toe voor deze site': 'Popup geblokkeerd - sta popups toe voor deze site',
    'Logo te groot (max 2 MB)': 'Logo te groot (max 2 MB)',
    'Geen huisnummer in adres': 'Geen huisnummer in adres',
    'Geen huisnummer gevonden in adresveld': 'Geen huisnummer gevonden in adresveld',
    'Activeer eerst een licentie om cloud te koppelen.': 'Activeer eerst een licentie om cloud te koppelen.',
    'Vul eerst postcode + huisnr in': 'Vul eerst postcode + huisnr in',
    'Vul eerst straat + huisnr in (bv. "Bagijnhof 33")': 'Vul eerst straat + huisnr in (bv. "Bagijnhof 33")',
    'Vul ook plaats in (autocomplete helpt)': 'Vul ook plaats in (autocomplete helpt)',
    'Vul e-mail en wachtwoord (min 6) in': 'Vul e-mail en wachtwoord (min 6) in',
    'Geef een geldige taalcode (bv. de)': 'Geef een geldige taalcode (bv. de)',
    'Kon verrekenen niet laden': 'Kon verrekenen niet laden',
    'E-mail verstuurd (mock)': 'E-mail verstuurd (mock)', 'Auto toevoegen (mock)': 'Auto toevoegen (mock)',
    'Export naar Excel (mock)': 'Export naar Excel (mock)', 'Layout opgeslagen (mock)': 'Layout opgeslagen (mock)',
    'Standaard hersteld (mock)': 'Standaard hersteld (mock)',
    'Dit is mock-data zonder echt bestand. Upload nieuwe documenten voor echte downloads.': 'Dit is mock-data zonder echt bestand. Upload nieuwe documenten voor echte downloads.',
    'Klant verwijderen?': 'Klant verwijderen?', 'Bon verwijderen?': 'Bon verwijderen?',
    'Auto verwijderen?': 'Auto verwijderen?', 'Onderdeel verwijderen?': 'Onderdeel verwijderen?',
    'Voertuig verwijderen?': 'Voertuig verwijderen?', 'Afspraak verwijderen?': 'Afspraak verwijderen?',
    'Document verwijderen?': 'Document verwijderen?', 'Logo verwijderen?': 'Logo verwijderen?',
    'Taal verwijderen?': 'Taal verwijderen?',
    'Account aangemaakt, maar mail kon niet verstuurd worden. Probeer "Opnieuw versturen".': 'Account aangemaakt, maar mail kon niet verstuurd worden. Probeer "Opnieuw versturen".',
  },
  en: {
    'nav.dashboard': 'Dashboard', 'nav.search': 'Search', 'nav.workcard': 'Work card',
    'nav.customers': 'Customers', 'nav.receipts': 'Receipts', 'nav.payments': 'Payments',
    'nav.cartrade': 'Car trading', 'nav.planning': 'Planning', 'nav.alerts': 'Alerts',
    'nav.parts': 'Parts', 'nav.reporting': 'Reporting', 'nav.settle': 'Settlements',
    'nav.documents': 'Documents', 'nav.layout': 'Receipt layout', 'nav.garage': 'Garage info',
    'nav.settings': 'Settings',
    'nav.diagnose': 'AI Diagnosis', 'nav.accounting': 'Accounting', 'nav.debtors': 'Accounts receivable',
    'nav.expenses': 'Costs / Purchase invoices', 'nav.reminders': 'Automatic reminders',
    'nav.vatprofit': 'VAT & Profit', 'nav.ledger': 'General ledger', 'nav.balance': 'Balance sheet',
    'nav.bankimport': 'Bank import', 'nav.onlinebooking': 'Online booking', 'nav.admaker': 'Create listing',
    'nav.partstrade': 'Parts trading', 'nav.oh_stock': 'Stock', 'nav.oh_used': 'Used parts',
    'nav.oh_new': 'New parts', 'nav.oh_scrap': 'Scrap cars', 'nav.oh_ads': 'Listings',
    'nav.oh_value': 'Valuation', 'nav.oh_history': 'Sales history',
    'page.dashboard.title': 'Dashboard', 'page.dashboard.sub': 'Overview of your garage',
    'page.customers.title': 'Customers', 'page.customers.sub': 'Customer management with vehicles and history',
    'page.receipts.title': 'Receipts', 'page.receipts.sub': 'Quotes, work orders and invoices',
    'page.payments.title': 'Payments', 'page.payments.sub': 'Registered payments and unpaid invoices',
    'page.cartrade.title': 'Car trading', 'page.cartrade.sub': 'Trading stock and sold vehicles',
    'page.planning.title': 'Planning', 'page.planning.sub': 'Appointments and work schedule',
    'page.alerts.title': 'Alerts', 'page.alerts.sub': 'MOT, unpaid invoices and dormant customers',
    'page.parts.title': 'Parts', 'page.parts.sub': 'Stock, purchase and sales prices',
    'page.reporting.title': 'Reporting', 'page.reporting.sub': 'Financial and operational reports',
    'page.settle.title': 'Settlements', 'page.settle.sub': 'Profit share per team member + advance costs',
    'page.documents.title': 'Documents', 'page.documents.sub': 'PDF management and cloud sync',
    'page.layout.title': 'Receipt layout', 'page.layout.sub': 'Customize the layout of quotes and invoices',
    'page.search.title': 'Search', 'page.search.sub': 'Search by license plate, customer name, receipt number or city',
    'page.workcard.title': 'Work card', 'page.workcard.sub': 'Open work and active repairs',
    'page.garage.title': 'Garage info', 'page.garage.sub': 'Used on all receipts, invoices and quotes',
    'page.settings.title': 'Settings', 'page.settings.sub': 'Manage license, cloud, team and backup',
    'tab.general': 'General', 'tab.plans': 'Plans', 'tab.languages': 'Languages',
    'tab.license': 'License', 'tab.cloud': 'Cloud sync', 'tab.team': 'Team',
    'tab.smtp': 'SMTP / Email', 'tab.backup': 'Backup', 'tab.notifications': 'Notifications', 'tab.maillog': 'Mail log',
    // Dashboard keys
    'dash.title': 'Dashboard', 'dash.subtitle': 'Overview of your garage',
    'dash.refresh_alerts': 'Refresh alerts', 'dash.today': 'Today',
    'dash.this_week': 'This week', 'dash.this_month': 'This month', 'dash.this_year': 'This year',
    'dash.vehicles': 'Vehicles', 'dash.open_amount': 'Open amount',
    'dash.revenue_per_month': 'Revenue per month (€) - last 12 mo',
    'dash.apk_alerts': 'MOT alerts (within 60 days)',
    'dash.top_customers': 'Top 5 customers by revenue',
    'dash.top_open': 'Top unpaid invoices',
    'dash.todays_planning': "Today's schedule",
    'dash.stock_alerts': 'Stock alerts (below minimum)',
    'dash.recent_receipts': 'Recent receipts',
    'dash.quick_actions': 'Quick actions',
    'dash.audit_trail': 'Audit trail (recent activity)',
    // Table columns
    'col.kenteken': 'License plate', 'col.merk_model': 'Make / Model',
    'col.apk': 'MOT', 'col.km': 'KM',
    // Common
    'common.open': 'Open', 'common.type': 'Type',
    // Generic Dutch words used in tables and labels (Dutch literal -> EN)
    'Kenteken': 'License plate', 'Merk / Model': 'Make / Model', 'Bj': 'Year',
    'Inkoop': 'Purchase', 'Verkoop': 'Sale', 'Marge': 'Margin', 'Status': 'Status',
    'Werkstatus': 'Work status', 'Bonnummer': 'Receipt no.', 'Datum': 'Date',
    'Klant': 'Customer', 'Klanten': 'Customers', 'Kentekens': 'License plates',
    'Methode': 'Method', 'Referentie': 'Reference', 'Bedrag': 'Amount',
    'Nummer': 'Number', 'Totaal': 'Total', 'Betaald': 'Paid',
    'Openstaand': 'Outstanding', 'Open': 'Open', 'Omschrijving': 'Description',
    'Aantal': 'Quantity', 'Prijs': 'Price', 'Code': 'Code', 'Naam': 'Name',
    'Voorraad': 'Stock', 'Min': 'Min', 'Voorraad / Min': 'Stock / Min',
    'Locatie': 'Location', 'Gebruiker': 'User', 'Actie': 'Action', 'Detail': 'Detail',
    'Grootte': 'Size', 'Plaats': 'City', 'Telefoon': 'Phone',
    'Rol': 'Role', 'Aan': 'To', 'Onderwerp': 'Subject',
    'Melding': 'Alert', 'Meldingen': 'Alerts', 'Prioriteit': 'Priority',
    'Start': 'Start', 'Eind': 'End', 'Duur': 'Duration', 'Titel': 'Title', 'Monteur': 'Mechanic',
    'Auto': 'Car', 'Bon': 'Receipt', 'Onderdeel': 'Part',
    'Persoon': 'Person', 'Gekoppelde bon': 'Linked receipt',
    'Winst%': 'Profit%', 'Aandeel omzet': 'Revenue share',
    'Voorgeschoten (open)': 'Advanced (open)', 'Saldo te ontvangen': 'Balance to receive',
    'Omzet (lifetime)': 'Revenue (lifetime)', 'Dagen': 'Days', 'Bedrag (€)': 'Amount (€)',
    'Notitie': 'Note', 'Notities': 'Notes',
    'Bouwjaar': 'Year', 'Brandstof': 'Fuel', 'APK vervalt': 'MOT expires',
    'KM-stand': 'Odometer', 'Postcode': 'Postal code', 'Huisnr': 'House no.',
    'Adres': 'Address', 'Land': 'Country', 'Iban': 'IBAN', 'KvK': 'CoC', 'BTW': 'VAT',
    'Garagenaam': 'Garage name', 'Wachtwoord': 'Password',
    'Inloggen': 'Sign in', 'Uitloggen': 'Sign out', 'Registreren': 'Register',
    'Nieuw': 'New', 'Reset': 'Reset',
    'Activeren': 'Activate', 'Deactiveren': 'Deactivate', 'Koppelen': 'Connect',
    'Loskoppelen': 'Disconnect', 'Downloaden': 'Download', 'Uploaden': 'Upload',
    'Versturen': 'Send', 'Verzenden': 'Send', 'Testen': 'Test',
    'Ja': 'Yes', 'Nee': 'No',
    'Vandaag': 'Today', 'Deze week': 'This week', 'Deze maand': 'This month', 'Dit jaar': 'This year',
    'Bonnen verdeling': 'Receipts breakdown', 'Offertes': 'Quotes', 'Werkbonnen': 'Work orders',
    'Facturen (open)': 'Invoices (open)', 'Facturen': 'Invoices',
    'Geen openstaande facturen.': 'No unpaid invoices.',
    'Geen afspraken vandaag.': 'No appointments today.',
    'Plan afspraak': 'Plan appointment', 'Alle voorraad is op peil.': 'All stock is at level.',
    'Nog geen bonnen.': 'No receipts yet.', 'Nog geen klant-omzet geregistreerd.': 'No customer revenue recorded yet.',
    'Afspraak': 'Appointment', 'Gepland': 'Planned', 'Bevestigd': 'Confirmed',
    'In uitvoering': 'In progress', 'Klaar': 'Done', 'Geannuleerd': 'Cancelled',
    'Concept': 'Draft', 'Bezig...': 'Loading...', 'Opzoeken': 'Look up',
    'Nieuwe klant': 'New customer', 'Nieuwe bon': 'New receipt', 'Map openen': 'Open folder',
    'geregistreerd': 'registered', 'klanten geregistreerd': 'customers registered',
    'Algemeen': 'General', 'Plannen': 'Plans', 'Talen': 'Languages',
    'Licentie': 'License', 'Cloud sync': 'Cloud sync', 'Team': 'Team',
    'Back-up': 'Backup', 'Mail log': 'Mail log',
    'Alle bevestigingen, herinneringen en facturen die via de app zijn verstuurd.':
      'All confirmations, reminders and invoices sent via the app.',
    'Nog geen e-mails verstuurd.': 'No emails sent yet.',
    'bon': 'invoice', 'herinnering': 'reminder', 'bevestiging': 'confirmation', 'manueel': 'manual',
    'Meldingen instellen': 'Notification settings',
    'Meldingen nu opnieuw genereren': 'Regenerate notifications now',
    '{count} meldingen geregenereerd': '{count} notifications regenerated',
    'Meldingen worden automatisch elke nacht gegenereerd op basis van APK-datum, openstaande bonnen en geplande afspraken. Klik hieronder om ze handmatig nu te verversen.':
      'Notifications are generated automatically every night based on MOT date, outstanding receipts and scheduled appointments. Click below to refresh manually.',
    // Backup tab
    '1. Download back-up': '1. Download backup',
    '2. Back-up herstellen (import)': '2. Restore backup (import)',
    '3. Back-up naar cloud (Pro/Enterprise)': '3. Backup to cloud (Pro/Enterprise)',
    'backup.download.desc': 'Download a complete JSON snapshot of all data (customers, vehicles, receipts, planning, stock, payments, etc). Keep this file in a safe place (Dropbox / Google Drive / external drive).',
    'backup.restore.desc': 'Upload a previously downloaded backup file to restore your data.',
    'Let op:': 'Note:',
    'backup.restore.warning': 'all current data in your garage will be replaced with the contents of the file.',
    'Download back-up': 'Download backup',
    'Back-up bestand selecteren': 'Select backup file',
    'Back-up nu naar cloud': 'Backup to cloud now',
    'backup.cloud.desc': 'Send a complete backup directly to your linked cloud (Dropbox / Google Drive). The file appears in your',
    'of': 'or', 'map.': 'folder.',
    'backup.cloud.licenseRequired': 'License required. Activate a PRO or ENTERPRISE license first under',
    'backup.cloud.notLinked': 'No cloud linked yet. Go to',
    'backup.cloud.notLinked.suffix': 'tab and connect Dropbox or Google Drive.',
    'backup.cloud.linked': 'Cloud linked:',
    'Laatste cloud back-up:': 'Last cloud backup:',
    'automatisch': 'automatic',
    'Auto-backup aan': 'Auto-backup on', 'Auto-backup uit': 'Auto-backup off',
    'Automatische nachtelijke back-up': 'Automatic nightly backup',
    'backup.auto.desc': 'we automatically send a fresh backup to your cloud folder every 24 hours.',
    'Cloud back-up gelukt': 'Cloud backup successful', 'Cloud back-up mislukt': 'Cloud backup failed',
    // Common toast / notification strings (NL literal -> EN)
    'Mislukt': 'Failed', 'Opslaan mislukt': 'Save failed', 'Verwijderen mislukt': 'Delete failed',
    'Opslaan': 'Save', 'Verwijderen': 'Delete', 'Annuleren': 'Cancel', 'Sluiten': 'Close',
    'Toevoegen': 'Add', 'Bewerken': 'Edit', 'Zoeken': 'Search', 'Exporteren': 'Export',
    'Verwijderd': 'Deleted', 'Opgeslagen': 'Saved', 'Toegevoegd': 'Added', 'Verrekend': 'Settled',
    'Klant verwijderd': 'Customer deleted', 'Klant opgeslagen': 'Customer saved',
    'Voertuig verwijderd': 'Vehicle deleted', 'Voertuig opgeslagen': 'Vehicle saved',
    'Bon aangemaakt': 'Receipt created', 'Bon opgeslagen': 'Receipt saved', 'Bon verwijderd': 'Receipt deleted',
    'Onderdeel opgeslagen': 'Part saved', 'Onderdeel verwijderd': 'Part deleted',
    'Auto verwijderd': 'Car deleted', 'Auto opgeslagen': 'Car saved',
    'Afspraak opgeslagen': 'Appointment saved', 'Afspraak verwijderd': 'Appointment deleted',
    'Document verwijderd': 'Document deleted', 'Geüpload': 'Uploaded',
    'Betaling toegevoegd': 'Payment added', 'Voorschot toegevoegd': 'Advance added',
    'Winstaandeel opgeslagen': 'Profit share saved', 'Medewerker toegevoegd': 'Team member added',
    'Algemene instellingen opgeslagen': 'General settings saved',
    'Garagegegevens opgeslagen': 'Garage info saved', 'Adres ingevuld': 'Address filled in',
    'Logo geüpload': 'Logo uploaded', 'Logo verwijderd': 'Logo deleted', 'SMTP opgeslagen': 'SMTP saved',
    'Cloud verbonden!': 'Cloud connected!', 'Cloud losgekoppeld': 'Cloud disconnected',
    'Licentie geactiveerd! Cloud functies zijn nu beschikbaar.': 'License activated! Cloud features are now available.',
    'Licentie gedeactiveerd': 'License deactivated', 'Back-up gedownload': 'Backup downloaded',
    'Welkom terug!': 'Welcome back!', 'Account aangemaakt! Check je e-mail om te activeren.': 'Account created! Check your email to activate.',
    'Activatiemail verstuurd. Check je inbox.': 'Activation email sent. Check your inbox.',
    'Taal verwijderd': 'Language deleted', 'Alle keys zijn al vertaald': 'All keys are already translated',
    'Auto-vertalen mislukt': 'Auto-translate failed', 'Laden mislukt': 'Load failed',
    'Toevoegen mislukt': 'Add failed', 'Activatie mislukt': 'Activation failed',
    'Back-up mislukt': 'Backup failed', 'Versturen mislukt': 'Send failed',
    'Verversen mislukt': 'Refresh failed', 'OAuth start mislukt': 'OAuth start failed',
    'SMTP test mislukt': 'SMTP test failed', 'E-mail mislukt': 'Email failed',
    'Omzetten mislukt': 'Conversion failed', 'Status update mislukt': 'Status update failed',
    'Download mislukt': 'Download failed', 'Upload mislukt': 'Upload failed',
    'RDW lookup mislukt': 'RDW lookup failed', 'Adres lookup mislukt': 'Address lookup failed',
    'PDOK lookup mislukt': 'PDOK lookup failed', 'Inloggen mislukt': 'Login failed',
    'Registratie mislukt': 'Registration failed', 'Opnieuw versturen mislukt': 'Resend failed',
    'Adres niet gevonden': 'Address not found', 'Kies een klant': 'Choose a customer',
    'Kies een bon': 'Choose a receipt', 'Geen klant gekoppeld': 'No customer linked',
    'Kenteken is verplicht': 'License plate required', 'Code is verplicht': 'Code required',
    'Naam is verplicht': 'Name required', 'Vul een titel in': 'Enter a title',
    'Vul een bedrag in': 'Enter an amount', 'Vul een omschrijving in': 'Enter a description',
    'Ongeldig bedrag': 'Invalid amount', 'Voer een licentiekey in': 'Enter a license key',
    'Vul eerst kenteken in': 'Enter license plate first', 'Vul eerst het kenteken in': 'Enter the license plate first',
    'Sla eerst de afspraak op': 'Save the appointment first',
    'Garagenaam verplicht': 'Garage name required', 'Wachtwoord minimaal 6 tekens': 'Password at least 6 characters',
    'Vul je e-mail in': 'Enter your email', 'Licentie vereist': 'License required',
    'Popup geblokkeerd - sta popups toe voor deze site': 'Popup blocked - allow popups for this site',
    'Logo te groot (max 2 MB)': 'Logo too large (max 2 MB)',
    'Geen huisnummer in adres': 'No house number in address',
    'Geen huisnummer gevonden in adresveld': 'No house number found in address field',
    'Activeer eerst een licentie om cloud te koppelen.': 'Activate a license first to link cloud.',
    'Vul eerst postcode + huisnr in': 'Enter postcode + house number first',
    'Vul eerst straat + huisnr in (bv. "Bagijnhof 33")': 'Enter street + house number first (e.g. "Bagijnhof 33")',
    'Vul ook plaats in (autocomplete helpt)': 'Also enter city (autocomplete helps)',
    'Vul e-mail en wachtwoord (min 6) in': 'Enter email and password (min 6)',
    'Geef een geldige taalcode (bv. de)': 'Provide a valid language code (e.g. de)',
    'Kon verrekenen niet laden': 'Failed to load settlements',
    'E-mail verstuurd (mock)': 'Email sent (mock)', 'Auto toevoegen (mock)': 'Add car (mock)',
    'Export naar Excel (mock)': 'Export to Excel (mock)', 'Layout opgeslagen (mock)': 'Layout saved (mock)',
    'Standaard hersteld (mock)': 'Defaults restored (mock)',
    'Dit is mock-data zonder echt bestand. Upload nieuwe documenten voor echte downloads.': 'This is mock data without a real file. Upload new documents for real downloads.',
    'Klant verwijderen?': 'Delete customer?', 'Bon verwijderen?': 'Delete receipt?',
    'Auto verwijderen?': 'Delete car?', 'Onderdeel verwijderen?': 'Delete part?',
    'Voertuig verwijderen?': 'Delete vehicle?', 'Afspraak verwijderen?': 'Delete appointment?',
    'Document verwijderen?': 'Delete document?', 'Logo verwijderen?': 'Delete logo?',
    'Taal verwijderen?': 'Delete language?',
    'Account aangemaakt, maar mail kon niet verstuurd worden. Probeer "Opnieuw versturen".': 'Account created, but email could not be sent. Try "Resend".',
    // Bulk additions — common strings still hardcoded across the app
    'Vul je e-mailadres in': 'Enter your email address',
    'Checkout mislukt': 'Checkout failed',
    'Gekopieerd!': 'Copied!',
    'Klant heeft geen e-mailadres': 'Customer has no email address',
    'Selecteer eerst een betaling': 'Select a payment first',
    'Sta pop-ups toe om het kwitantie-document te openen': 'Allow pop-ups to open the receipt document',
    'Fooi moet tussen 0 en het bedrag liggen': 'Tip must be between 0 and the amount',
    'Selecteer eerst een afspraak': 'Select an appointment first',
    'Selecteer eerst een klant': 'Select a customer first',
    'Selecteer eerst een bon': 'Select a receipt first',
    'Bewaren': 'Save', 'Ververs': 'Refresh',
    'Importeren': 'Import', 'Printen': 'Print', 'Mailen': 'Email',
    'Genereren': 'Generate',
    'Nieuwe afspraak': 'New appointment', 'Nieuwe auto': 'New car', 'Nieuwe melding': 'New notification',
    'Nieuw onderdeel': 'New part', 'Nieuw document': 'New document', 'Nieuwe taal': 'New language',
    'Reservering': 'Reservation', 'Verkocht': 'Sold', 'Overschreven': 'Transferred',
    'Geschorst': 'Suspended', 'Gearchiveerd': 'Archived', 'Actief': 'Active', 'Slapend': 'Dormant',
    'Zakelijk': 'Business', 'Particulier': 'Private', 'Verzonden': 'Sent',
    'Onbetaald': 'Unpaid', 'Afgerond': 'Completed',
    'Winst': 'Profit', 'Kosten': 'Costs', 'Subtotaal': 'Subtotal',
    'Betaling': 'Payment', 'Fooi': 'Tip', 'Voorschot': 'Advance', 'Voorgeschoten': 'Advanced',
    'Voorgeschoten door': 'Advanced by',
    'Voertuig': 'Vehicle', 'Voertuigen': 'Vehicles',
    'Autos': 'Cars',
    'Tijd': 'Time', 'Periode': 'Period',
    'Aangepast': 'Custom', 'Gisteren': 'Yesterday',
    'Geen resultaten': 'No results', 'Geen data': 'No data', 'Laden...': 'Loading...',
    'Bezig met laden': 'Loading', 'Verwerken...': 'Processing...',
    'Gelukt': 'Success', 'Succesvol': 'Successful',
    'Auto toevoegen': 'Add car',
    'Vervangen onderdelen / kosten': 'Replaced parts / costs',
    'Nog geen kostenposten. Klik + Toevoegen om bijvoorbeeld een vervangen onderdeel of voorgeschoten kosten te registreren.':
      'No cost items yet. Click + Add to register e.g. a replaced part or advanced costs.',
    'Totaal kosten:': 'Total costs:',
    'Open voorschot': 'Open advance', 'Bedrijfsinformatie': 'Business information',
    'Bedrijfsnaam': 'Company name', 'Bedrijfslogo op factuur': 'Company logo on invoice',
    'Hoofdvoertuig': 'Primary vehicle', 'Hoofdvoertuig instellen': 'Set as primary vehicle',
    'Geen voorschotten gevonden': 'No advances found',
    'Concept opgeslagen': 'Draft saved',
    'Mail verstuurd': 'Email sent', 'Bevestiging verstuurd': 'Confirmation sent',
    'Herinnering verstuurd': 'Reminder sent',
    'Wachtwoord gewijzigd': 'Password changed', 'Wachtwoord wijzigen': 'Change password',
    'Naam ingevuld': 'Name filled in',
    'Voer een geldig e-mailadres in': 'Enter a valid email address',
    'Geen omschrijving': 'No description', 'Geen klant geselecteerd': 'No customer selected',
    'Geen bon geselecteerd': 'No receipt selected', 'Geen auto geselecteerd': 'No car selected',
    'Niets te tonen': 'Nothing to show', 'Niets gevonden': 'Nothing found',
    'Lijst bijgewerkt': 'List updated', 'Wijzigingen opgeslagen': 'Changes saved',
    'Wijzigingen niet opgeslagen': 'Changes not saved',
    'Alleen voor PRO/Enterprise plan': 'PRO/Enterprise only', 'Upgrade vereist': 'Upgrade required',
    // Dashboard sweep additions
    'Jouw plan': 'Your plan',
    'Gratis variant - upgrade voor onbeperkt gebruik en cloud sync.': 'Free plan - upgrade for unlimited use and cloud sync.',
    'Actief plan met': 'Active plan with', 'team-seat(s)': 'team seat(s)', 'beschikbaar': 'available', 'uit': 'off',
    'Bonnen (maand)': 'Receipts (month)', 'Teamleden': 'Team members',
    'Upgrade': 'Upgrade', 'Beheren': 'Manage',
    'meldingen ververst': 'alerts refreshed', 'vervalt': 'expires',
    'Nog geen omzet geregistreerd. Maak je eerste factuur of werkbon aan om hier de grafiek te zien.': 'No revenue recorded yet. Create your first invoice or work order to see the chart here.',
    "Geen APK's vervallen binnenkort.": 'No MOTs expiring soon.',
    'Nog geen activiteit geregistreerd.': 'No activity recorded yet.',
    'Klant aanmaken (F1)': 'Create customer (F1)',
    'Werkbon / Offerte / Factuur': 'Work order / Quote / Invoice',
    'Open lopend werk': 'Open active work',
    'Afspraken vandaag': "Today's appointments",
    'actief': 'active', 'PDF beheer': 'PDF management',
    'Adres ophalen': 'Fetch address', 'Postcode + huisnr': 'Postcode + house no.',
    'RDW check': 'RDW check', 'Kenteken opzoeken': 'Look up license plate',
    'Welkom bij MonteurCentraal!': 'Welcome to MonteurCentraal!',
    'Begin met deze 3 stappen om je garage op te zetten:': 'Start with these 3 steps to set up your garage:',
    'Voeg je garagegegevens toe in': 'Add your garage details in',
    '(KvK, BTW, IBAN voor op de factuur)': '(CoC, VAT, IBAN for the invoice)',
    'Maak een eerste klant + voertuig aan via': 'Create a first customer + vehicle via',
    '(RDW lookup vult automatisch in)': '(RDW lookup fills in automatically)',
    'Maak je eerste werkbon of factuur via': 'Create your first work order or invoice via',
    'RDW kenteken check': 'RDW license plate check', 'Gevonden': 'Found',
    'Type voertuig': 'Vehicle type', 'Kleur': 'Color',
    'Adres opzoeken': 'Look up address', 'Postcode (1011 AA)': 'Postcode (1011 AA)',
    'Huisnummer': 'House number', 'Straat + nr': 'Street + no.',
    'Gemeente': 'Municipality', 'Provincie': 'Province',
    'Layout opgeslagen': 'Layout saved', 'Standaard hersteld': 'Defaults restored',
    'Editor': 'Editor', 'E-mail niet geconfigureerd': 'Email not configured',
    'afspraken': 'appointments', 'Weekagenda': 'Week calendar', 'Lijstweergave': 'List view',
    'Bevestiging': 'Confirmation', 'Herinnering': 'Reminder',
    'Afspraak bewerken': 'Edit appointment',
    'Geen monteur': 'No mechanic',
    'Voeg eerst teamleden toe': 'First add team members in Settings → Team',
    'Algemene instellingen': 'General settings', 'Licentie status': 'License status',
    'Team / Medewerkers': 'Team / Employees', 'Mijn SMTP instellingen': 'My SMTP settings',
    'Top verkochte voertuigen': 'Top sold vehicles', 'Bonnen samenvatting': 'Receipts summary',
    'Winstverdeling per teamlid': 'Profit distribution per team member',
    'klik op een kenteken voor volledige historie': 'click a license plate for full history',
    'Gratis': 'Free', '/ maand': '/ month', 'Koop': 'Buy',
    'Jouw huidige plan': 'Your current plan',
    'Kies het plan dat past bij jouw garage': 'Choose the plan that fits your garage',
    'Kies het plan dat bij jouw garage past': 'Choose the plan that fits your garage',
    'Geen verborgen kosten. Upgrade of downgrade op elk moment via je licentie key.': 'No hidden costs. Upgrade or downgrade anytime via your license key.',
    'Reeds betaald': 'Already paid', 'geen regels': 'no lines', 'Korting': 'Discount',
    'Afdrukken / Opslaan als PDF': 'Print / Save as PDF',
    'Bedankt voor uw vertrouwen in': 'Thank you for your trust in',
    'Gelieve het openstaande bedrag binnen 14 dagen over te maken naar IBAN': 'Please transfer the outstanding amount within 14 days to IBAN',
    'onder vermelding van bonnummer': 'quoting receipt number',
    'Geen BTW van toepassing (hobby/particulier)': 'No VAT applicable (hobby/private)',
    'documenten in archief': 'documents in archive',
    'actieve meldingen die aandacht vragen': 'active alerts requiring attention',
    'artikelen': 'items', 'onder minimum voorraad': 'below minimum stock',
    'bonnen in behandeling': 'receipts in progress', 'Nieuwe werkbon': 'New work order',
    'Geen voertuigen gevonden.': 'No vehicles found.', 'Selecteer een voertuig.': 'Select a vehicle.',
    'Geen bonnen gevonden.': 'No receipts found.', 'Selecteer een bon.': 'Select a receipt.',
    'Geen klanten gevonden.': 'No customers found.', 'Selecteer een klant.': 'Select a customer.',
    'Geen onderdelen gevonden.': 'No parts found.', 'Geen documenten gevonden.': 'No documents found.',
    'Geen teamleden.': 'No team members.', 'Geen voorschotten in deze categorie.': 'No advances in this category.',
    'Nog geen verkopen geregistreerd.': 'No sales recorded yet.',
    'Geen resultaten gevonden voor': 'No results found for',
    'Nog geen bonnen geregistreerd voor dit voertuig.': 'No receipts recorded for this vehicle yet.',
    'Geen logo': 'No logo',
    'BASIC (gratis)': 'BASIC (free)',
    'Maandag': 'Monday', 'Dinsdag': 'Tuesday', 'Woensdag': 'Wednesday', 'Donderdag': 'Thursday',
    'Vrijdag': 'Friday', 'Zaterdag': 'Saturday', 'Zondag': 'Sunday',
    '24/7 uren tonen': 'Show 24/7 hours', 'Alle monteurs': 'All mechanics',
    'Vergeet niet': "Don't forget", 'Negeren': 'Dismiss', 'Suggesties': 'Suggestions',
    'Slimme suggesties': 'Smart suggestions', 'Suggestieregels opgeslagen': 'Suggestion rules saved',
    'Standaardlijst hersteld': 'Default list restored', 'Standaardlijst': 'Default list',
    'Regel toevoegen': 'Add rule', 'Trefwoorden': 'Keywords', 'Tip-tekst': 'Tip text',
    'Onderwerp': 'Subject', 'Geen regels.': 'No rules.',
    '(eigen lijst actief)': '(custom list active)', '(standaardlijst actief)': '(default list active)',
    'Terug naar de standaardlijst? Je eigen aanpassingen vervallen.': 'Restore the default list? Your customizations will be lost.',
    "Wanneer een bonregel een van de trefwoorden bevat, toont de bon-editor eenmalig een tip. Trefwoorden en suggesties scheid je met komma's; álle trefwoorden moeten voorkomen.": 'When a receipt line contains the keywords, the editor shows a tip once. Separate keywords with commas; all keywords must be present.',
    'VIN check': 'VIN check', 'Chassisnummer opzoeken': 'Look up chassis number',
    'Chassisnummer (VIN)': 'Chassis number (VIN)', 'Chassisnummer check (VIN)': 'Chassis number check (VIN)',
    'VIN lookup mislukt': 'VIN lookup failed', 'Vul eerst het chassisnummer in': 'Enter the chassis number first',
    "handig bij buitenlandse auto's": 'useful for foreign cars',
    'Haal merk/model/bouwjaar op via chassisnummer': 'Fetch make/model/year via chassis number',
    'Carrosserie': 'Body type', 'Motor': 'Engine', 'Productieland': 'Country of manufacture', 'Uitvoering': 'Trim',
    "Let op: een VIN bevat geen kleur, kenteken of APK-datum. Bij Europese auto's kan het model beperkt gedecodeerd worden.": "Note: a VIN does not contain colour, license plate or MOT date. For European cars the model may be only partially decoded.",
    // Sweep additions (Klanten/Bonnen/Verrekenen/Instellingen/BonEditor)
    'Werkbon': 'Work order', 'Offerte': 'Quote', 'Factuur': 'Invoice', 'Alle': 'All',
    'Arbeid': 'Labour', 'ExtraKosten': 'Extra costs', 'Tekst': 'Text',
    'BetaaldBank': 'Paid (bank)', 'BetaaldContant': 'Paid (cash)', 'DeelsBetaald': 'Partially paid',
    'Gefactureerd': 'Invoiced', 'Akkoord': 'Approved', 'OfferteVerstuurd': 'Quote sent',
    'WachtenOpParts': 'Waiting for parts', 'InUitvoering': 'In progress', 'Wacht op onderdelen': 'Waiting for parts',
    'Openen': 'Open', 'Mail': 'Email', 'E-mail': 'Email', 'Omgezet naar': 'Converted to', 'E-mail verstuurd': 'Email sent',
    'door': 'by', 'Workflow': 'Workflow', 'Verstuurd': 'Sent', 'Regels': 'Lines',
    'Klant bewerken': 'Edit customer', 'Postcode / Plaats': 'Postcode / City',
    'Laatst gezien': 'Last seen', 'Laatst gefactureerd': 'Last invoiced',
    'Voertuig toevoegen': 'Add vehicle', 'Maak hoofdvoertuig': 'Make primary vehicle',
    'HOOFD': 'MAIN', 'N.v.t.': 'N/A', 'Voertuig vrijgesteld van APK': 'Vehicle exempt from MOT',
    'Dubbelklik om te bewerken': 'Double-click to edit', 'bellen': 'call',
    'Open documenten van': 'Open documents of',
    'Zoek op naam, plaats, telefoon, kenteken...': 'Search by name, city, phone, license plate...',
    'Zoek op nummer, klant, omschrijving, kenteken...': 'Search by number, customer, description, license plate...',
    'Adres (straat + huisnummer)': 'Address (street + house number)',
    'Vul in via postcode': 'Fill in via postcode', 'Vul postcode via straat + plaats': 'Find postcode via street + city',
    'Verversen': 'Refresh', 'Nieuw voorschot': 'New advance',
    'Totale omzet (lifetime)': 'Total revenue (lifetime)', 'Totaal ontvangen betalingen': 'Total payments received',
    'Som winstaandelen': 'Sum of profit shares', 'Eigenaar': 'Owner', 'Medewerker': 'Employee',
    'Wijzig': 'Change', 'Hoe werkt het?': 'How does it work?', 'Voorschotten': 'Advances',
    'Markeer als verrekend': 'Mark as settled', 'Wie heeft voorgeschoten?': 'Who advanced the money?',
    'geen': 'none', 'optioneel': 'optional', 'Winstaandeel': 'Profit share',
    'onbekend': 'unknown', 'Backup herstellen?': 'Restore backup?', 'Export datum': 'Export date',
    'Taal': 'Language', 'Standaard BTW%': 'Default VAT%', 'Uurtarief arbeid (€)': 'Hourly labour rate (€)',
    'Hobby modus': 'Hobby mode', 'geen BTW': 'no VAT', 'Licentie actief': 'License active', 'Plan': 'Plan',
    'Max klanten': 'Max customers', 'Max bonnen/mnd': 'Max receipts/mo', 'Max teamleden': 'Max team members',
    'Onbeperkt': 'Unlimited', 'dagen': 'days', 'Auto-handel': 'Car trading', 'Audit log': 'Audit log',
    'Licentie deactiveren': 'Deactivate license', 'Licentiekey invoeren': 'Enter license key',
    'Verbonden met': 'Connected to', 'Account': 'Account', 'Cloud loskoppelen': 'Disconnect cloud',
    'Alle medewerkers binnen': 'All employees within', 'zien dezelfde data.': 'see the same data.',
    'Medewerker uitnodigen': 'Invite employee', 'Tijdelijk wachtwoord (min 6)': 'Temporary password (min 6)',
    'Uitnodigen': 'Invite', 'Gmail tip:': 'Gmail tip:', 'gebruik een': 'use an',
    'Poort': 'Port', 'E-mail / gebruikersnaam': 'Email / username', 'Afzendernaam (optioneel)': 'Sender name (optional)',
    'Testbericht verzonden naar': 'Test message sent to', 'Test verzenden': 'Send test',
    'Binnenkort beschikbaar': 'Coming soon', 'Niet geconfigureerd': 'Not configured',
    'Klik om te verbinden': 'Click to connect', 'Verbind': 'Connect',
    'bewerken': 'edit', 'Kies kenteken': 'Choose license plate', 'Niemand': 'Nobody',
    'Prijs (€)': 'Price (€)', 'Vast (€)': 'Fixed (€)', 'Percentage (%)': 'Percentage (%)',
    'Na korting': 'After discount', 'Hobby modus - geen BTW van toepassing': 'Hobby mode - no VAT applicable',
    'Voeg regels toe via de knoppen hierboven.': 'Add lines via the buttons above.',
    'optioneel — wie heeft onderdelen/kosten betaald': 'optional — who paid for parts/costs',
    'Bv. Jan de Vries': 'E.g. John Smith', 'bv. Medemblik': 'e.g. Medemblik',
    'bv. Bagijnhof 33 of Kerkstraat 5': 'e.g. Bagijnhof 33 or Kerkstraat 5',
    'bv. Onderdelen bij Kroymans gehaald': 'e.g. Parts bought at Kroymans',
    'Geen licentie?': 'No license?', 'Beschikbare plannen': 'Available plans',
    'Plan afspraak': 'Plan appointment',
  },
};

// Volledige statische EN-tabel (hardcoded, geen runtime-LLM). Gegenereerde basis,
// handmatig gecureerde inline-en (hierboven) wint bij overlap.
FALLBACK.en = { ...EN_GENERATED, ...EN_PUBLIC_EXTRA, ...FALLBACK.en };
// Volledige statische ES-tabel (hardcoded, geen runtime-LLM) — net als EN.
FALLBACK.es = { ...ES_GENERATED, ...ES_PUBLIC_EXTRA, ...(FALLBACK.es || {}) };

// Talen die volledig hardcoded zijn (statische tabel, nooit runtime-LLM/autofill).
const STATIC_LANGS = new Set(['en', 'es']);

const BUILTIN_LANGS = [
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
];

const LanguageContext = createContext({
  lang: 'nl', setLang: () => {}, t: (k) => k,
  languages: BUILTIN_LANGS, refreshLanguages: () => {}, entries: {},
  autoDetected: false, dismissAutoDetected: () => {},
});

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem(STORAGE_LANG) || 'nl');
  const [languages, setLanguages] = useState(BUILTIN_LANGS);
  const [entries, setEntries] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(STORAGE_CACHE) || '{}');
      return cached[localStorage.getItem(STORAGE_LANG) || 'nl'] || FALLBACK[localStorage.getItem(STORAGE_LANG) || 'nl'] || FALLBACK.nl;
    } catch { return FALLBACK.nl; }
  });
  const loadedRef = useRef({});
  const [autoDetected, setAutoDetected] = useState(false);

  const setLang = (l) => {
    setLangState(l);
    localStorage.setItem(STORAGE_LANG, l);
    setAutoDetected(false);
    // Persisteer de taalvoorkeur bij het gebruikersaccount (volgt de gebruiker bij inloggen)
    const tok = localStorage.getItem('monteurcentraal_token');
    if (tok) {
      axios.post(`${BASE}/api/auth/me/language`, { lang: l }, { headers: { Authorization: `Bearer ${tok}` } }).catch(() => {});
    }
  };
  // Pas de taal van een ingelogde gebruiker toe (vanuit hun account) zonder opnieuw te persisteren
  const applyAccountLang = (l) => {
    if (l && l !== lang) {
      setLangState(l);
      localStorage.setItem(STORAGE_LANG, l);
      setAutoDetected(false);
    }
  };
  const dismissAutoDetected = () => setAutoDetected(false);

  // Laad vertalingen voor de actieve taal vanaf de backend
  // (ingelogd: incl. garage-overrides; uitgelogd: publieke/builtin vertalingen)
  useEffect(() => {
    const tok = localStorage.getItem('monteurcentraal_token');
    const url = tok ? `${BASE}/api/i18n/translations/${lang}` : `${BASE}/api/i18n/public/${lang}`;
    const cfg = tok ? { headers: { Authorization: `Bearer ${tok}` } } : undefined;
    Promise.resolve()
      .then(() => axios.get(url, cfg))
      .then(r => {
        const e = r.data?.entries || {};
        setEntries(e);
        loadedRef.current[lang] = e;
        try {
          const cache = JSON.parse(localStorage.getItem(STORAGE_CACHE) || '{}');
          cache[lang] = e;
          localStorage.setItem(STORAGE_CACHE, JSON.stringify(cache));
        } catch (_e) { /* ignore localStorage errors */ }
      })
      .catch(() => {
        setEntries(FALLBACK[lang] || FALLBACK.nl);
      });
  }, [lang]);

  // Laad lijst beschikbare talen (ingelogd: garage-talen; uitgelogd: publieke lijst)
  useEffect(() => {
    const tok = localStorage.getItem('monteurcentraal_token');
    const url = tok ? `${BASE}/api/i18n/languages` : `${BASE}/api/i18n/public/languages`;
    const cfg = tok ? { headers: { Authorization: `Bearer ${tok}` } } : undefined;
    Promise.resolve()
      .then(() => axios.get(url, cfg))
      .then(r => setLanguages(r.data || BUILTIN_LANGS))
      .catch(() => setLanguages(BUILTIN_LANGS));
  }, []);

  // Browsertaal-detectie voor eerste-keer PUBLIEKE bezoekers (NL/EN/DE).
  // Ingelogde gebruikers en bezoekers die al een taal kozen behouden hun keuze.
  useEffect(() => {
    const tok = localStorage.getItem('monteurcentraal_token');
    if (tok || localStorage.getItem(STORAGE_LANG)) return;
    const supported = ['nl', 'en', 'de'];
    const navs = (navigator.languages && navigator.languages.length) ? navigator.languages : [navigator.language || 'nl'];
    const match = navs.map(s => String(s).slice(0, 2).toLowerCase()).find(l => supported.includes(l));
    if (match && match !== lang) {
      setLangState(match);
      localStorage.setItem(STORAGE_LANG, match);
      setAutoDetected(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshLanguages = React.useCallback(() => {
    const tok = localStorage.getItem('monteurcentraal_token');
    if (!tok) return Promise.resolve();
    return Promise.all([
      axios.get(`${BASE}/api/i18n/languages`, { headers: { Authorization: `Bearer ${tok}` } }).then(r => setLanguages(r.data || BUILTIN_LANGS)),
      axios.get(`${BASE}/api/i18n/translations/${lang}`, { headers: { Authorization: `Bearer ${tok}` } }).then(r => setEntries(r.data?.entries || {})),
    ]).catch(() => {});
  }, [lang]);

  // ---- Auto-translate missing keys via Gemini backend ----
  const pendingKeysRef = useRef(new Map()); // key -> Nederlandse bron-tekst
  const flushTimerRef = useRef(null);

  const requestAutofill = React.useCallback((key, source) => {
    if (!key || lang === 'nl') return;
    pendingKeysRef.current.set(key, source || key);
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(async () => {
      const tok = localStorage.getItem('monteurcentraal_token');
      const pending = Array.from(pendingKeysRef.current.entries()).slice(0, 100);
      pendingKeysRef.current = new Map();
      if (pending.length === 0) return;
      const keys = pending.map(([k]) => k);
      const sources = {};
      pending.forEach(([k, src]) => { if (src && src !== k) sources[k] = src; });
      try {
        const url = tok ? `${BASE}/api/i18n/autofill` : `${BASE}/api/i18n/public/autofill`;
        const cfg = tok ? { headers: { Authorization: `Bearer ${tok}` } } : undefined;
        const r = await axios.post(url, { lang, keys, sources }, cfg);
        const newEntries = r.data?.translated || {};
        if (Object.keys(newEntries).length > 0) {
          setEntries(prev => {
            const merged = { ...prev, ...newEntries };
            try {
              const cache = JSON.parse(localStorage.getItem(STORAGE_CACHE) || '{}');
              cache[lang] = merged;
              localStorage.setItem(STORAGE_CACHE, JSON.stringify(cache));
            } catch (_e) { /* noop */ }
            return merged;
          });
        }
      } catch (_e) { /* netwerk-fouten stil; volgende t() call probeert opnieuw */ }
    }, 1200);
  }, [lang]);

  const value = useMemo(() => ({
    lang, setLang, applyAccountLang, languages, refreshLanguages, entries, autoDetected, dismissAutoDetected,
    t: (key, fallback) => {
      if (!key) return fallback || '';
      // Negeer 'vervuilde' cache-waardes die gelijk zijn aan de key zelf (mislukte autofill)
      if (entries[key] && entries[key] !== key) return entries[key];
      if (FALLBACK[lang]?.[key]) return FALLBACK[lang][key];
      // Onbekende key voor niet-NL taal → vraag Gemini in achtergrond.
      // EN & ES zijn volledig hardcoded (FALLBACK) → nooit LLM/autofill voor die talen.
      // i18n-identifiers (bv. 'nav.purchasing') worden vertaald via hun NL-fallback-tekst.
      if (lang !== 'nl' && lang !== 'NL' && !STATIC_LANGS.has(lang)) {
        if (isI18nKey(key)) {
          if (fallback) requestAutofill(key, fallback);
        } else {
          requestAutofill(key, key);
        }
      }
      return fallback || key;
    },
  }), [lang, languages, entries, refreshLanguages, requestAutofill, autoDetected]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useT() {
  return useContext(LanguageContext);
}
